import { FC, useCallback } from "react";
import { Button, List, ListItem, Popover, PopoverOrigin } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ResourceExplorerBudgetOption } from "./ResourceExplorerBudgetOption";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resourceExplorerViewBudgetsSelector } from "@/store/resource-explorer/selectors/budget/resourceExplorerViewBudgetsSelector";
import {
  initiateBudgetForCreate,
  setCurrentBudgetData,
} from "@/store/budgets/budgetsSlice";
import { getResourceExplorerDataByBudgetThunk } from "@/store/budgets/thunks/getResourceExplorerDataByBudgetThunk";
import { toggleAlertsOpenDrawer } from "@/store/alerts/alertsSlice";
import { resourceExplorerIdSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/resourceExplorerIdSelector";
import { useAppAbility } from "@/services/permissions";
import { updateBillingExplorerChartSettingsThunk } from "@/store/resource-explorer/thunks/resource-explorer/updateBillingExplorerChartSettingsThunk";

interface ResourceExplorerBudgetsPopoverProps {
  open: boolean;
  closeMenu(): void;
  anchor: HTMLElement | null;
  selectedBudgetId: string;
}

export const ResourceExplorerBudgetsPopover: FC<
  ResourceExplorerBudgetsPopoverProps
> = ({ open, closeMenu, anchor, selectedBudgetId }) => {
  const dispatch = useAppDispatch();
  const budgets = useAppSelector(resourceExplorerViewBudgetsSelector);
  const viewId = useAppSelector(resourceExplorerIdSelector);
  const { can } = useAppAbility();
  const canCreateBudget = can("create", "budget");

  const handleBudgetClick = useCallback(
    (id: string) => {
      dispatch(
        updateBillingExplorerChartSettingsThunk({
          selected_budget_id: id,
        }),
      );
      closeMenu();
    },
    [dispatch, closeMenu],
  );

  const toggleBudgetsAndAlertsDrawer = useCallback(() => {
    dispatch(initiateBudgetForCreate());
    dispatch(setCurrentBudgetData({ viewId }));
    dispatch(getResourceExplorerDataByBudgetThunk());
    dispatch(toggleAlertsOpenDrawer());

    closeMenu();
  }, [dispatch, viewId, closeMenu]);

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={closeMenu}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      <List>
        {budgets?.map((budget) => {
          return (
            <ResourceExplorerBudgetOption
              key={budget.id}
              budget={budget}
              selected={budget.id === selectedBudgetId}
              onClick={() => handleBudgetClick(budget.id)}
            />
          );
        })}

        {canCreateBudget && (
          <ListItem sx={{ p: 0, borderTop: 1, borderColor: "grey.300" }}>
            <Button
              onClick={toggleBudgetsAndAlertsDrawer}
              startIcon={<AddIcon />}
              fullWidth
              sx={{ justifyContent: "start", px: 2, py: 1 }}
            >
              Create Budget
            </Button>
          </ListItem>
        )}
      </List>
    </Popover>
  );
};

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "left",
};
const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "left",
};
